import { useState } from 'react';
import {
    Box,
    Button,
    FlexBox,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    SelectionSetTileImageContainer,
    SelectionSetTileName,
    Typography,
} from '@vp/swan';
import { HelpMeAnswerButton } from './HelpMeAnswerButton';
import type { ColorOption } from '../types/selectors';

export type { ColorOption };

export interface ColorSelectorProps {
    options: ColorOption[];
    maxSelections?: number;
    defaultValues?: string[];
    onComplete: (selectedOptions: ColorOption[]) => void;
}

export function ColorSelector({
    options,
    maxSelections = 3,
    defaultValues,
    onComplete,
}: ColorSelectorProps) {
    const [selectedColors, setSelectedColors] = useState<string[]>(defaultValues ?? []);
    const [maxReachedMessage, setMaxReachedMessage] = useState('');

    // Derive the selectedValues map Swan expects: { [value]: true/false }
    const selectedValuesMap: Record<string, boolean> = Object.fromEntries(
        options.map((o) => [o.value, selectedColors.includes(o.value)])
    );

    const handleSelectedValuesChange = (newSelectedValues: Record<string, boolean>) => {
        const selected = Object.keys(newSelectedValues).filter((k) => newSelectedValues[k]);
        if (selected.length <= maxSelections) {
            setSelectedColors(selected);
            setMaxReachedMessage('');
        } else {
            setMaxReachedMessage(`Maximum of ${maxSelections} colors selected`);
        }
    };

    const handleContinue = () => {
        if (selectedColors.length > 0) {
            onComplete(options.filter((o) => selectedColors.includes(o.value)));
        }
    };

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding:
                    'var(--swan-sem-space-6) var(--swan-sem-space-6) var(--swan-sem-space-3) var(--swan-sem-space-6)',
                borderRadius:
                    'var(--swan-sem-border-radius-standard) var(--swan-sem-border-radius-standard) 0 0',
            }}
        >
            <Box mb={4} mt={1}>
                <Typography
                    fontSkin="title-item"
                    style={{
                        paddingBottom: 'var(--swan-sem-space-3)',
                    }}
                >
                    Colors to explore{' '}
                    <Typography as="span" textColor="subtle" fontSkin="body-standard">
                        (Select up to {maxSelections}
                        {selectedColors.length > 0 &&
                            ` - ${selectedColors.length}/${maxSelections} selected`}
                        )
                    </Typography>
                </Typography>
            </Box>

            <Box>
                <SelectionSet
                    skin="tiles-vertical"
                    variant="multi-select"
                    selectedValues={selectedValuesMap}
                    onSelectedValuesChange={handleSelectedValuesChange}
                    aria-label="Select up to 3 color palettes"
                >
                    {options.map((option) => (
                        <SelectionSetInput key={option.value} value={option.value}>
                            <SelectionSetLabel style={{ minHeight: 'auto', height: 'auto' }}>
                                <SelectionSetTileImageContainer
                                    style={{
                                        height: '40px',
                                    }}
                                >
                                    <img
                                        src={option.imageUrl}
                                        alt={option.name}
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            objectFit: 'fill',
                                            display: 'block',
                                        }}
                                    />
                                </SelectionSetTileImageContainer>
                                <SelectionSetTileName
                                    style={{
                                        textAlign: 'left',
                                        padding:
                                            'var(--swan-sem-space-2) var(--swan-sem-space-3) var(--swan-sem-space-4) var(--swan-sem-space-3)',
                                        marginTop: 'var(--swan-sem-space-2)',
                                    }}
                                >
                                    {option.name}
                                </SelectionSetTileName>
                            </SelectionSetLabel>
                        </SelectionSetInput>
                    ))}
                </SelectionSet>
                <span aria-live="assertive" aria-atomic="true" className="sr-only">
                    {maxReachedMessage}
                </span>
            </Box>

            <FlexBox gap={4} justifyContent="space-between" alignItems="center">
                <HelpMeAnswerButton
                    selectorType="color-selector"
                    options={options.map(({ name }) => ({ name }))}
                />
                <Button
                    skin="secondary"
                    onClick={handleContinue}
                    disabled={selectedColors.length === 0}
                >
                    Continue
                </Button>
            </FlexBox>
        </Box>
    );
}
